import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmt, STATUS, MAT_TYPE, MANUAL_TYPES } from "@/lib/ui";
import { AlertTriangle, PackageX, Clock, ShieldCheck, TrendingDown } from "lucide-react";

function Card({ children, className = "", ...props }) {
  return (
    <div className={`bg-[#131A26] border border-[#212D40] rounded-xl ${className}`} {...props}>
      {children}
    </div>
  );
}

const sum4 = (weekly, field) =>
  (weekly || []).slice(0, 4).reduce((a, w) => a + (w[field] || 0), 0);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("");

  useEffect(() => {
    api.get("/projection?weeks=12").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-[#94A3B8]">Yükleniyor...</div>;

  const critical = data.materials.filter((m) => m.status === "kritik");
  const warning = data.materials.filter((m) => m.status === "uyari");

  const cards = [
    { label: "Kritik Malzeme", value: data.summary.kritik, icon: PackageX, color: "#EF4444" },
    { label: "Riskli (Uyarı)", value: data.summary.uyari, icon: AlertTriangle, color: "#F59E0B" },
    { label: "Güvenli Stok", value: data.summary.guvenli, icon: ShieldCheck, color: "#10B981" },
    {
      label: "Acil Sipariş",
      value: data.materials.filter((m) => m.reorder_status === "acil").length,
      icon: Clock, color: "#0EA5E9",
    },
  ];

  return (
    <div className="space-y-6 animate-in-up" data-testid="dashboard-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight">Genel Bakış</h1>
        <p className="text-[#94A3B8] text-sm mt-1">
          Önümüzdeki 12 hafta için stok projeksiyonu ve sipariş uyarıları
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-summary-cards">
        {cards.map((c) => (
          <Card key={c.label} className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#94A3B8]">{c.label}</div>
                <div className="font-num text-3xl font-bold mt-2" style={{ color: c.color }}>{c.value}</div>
              </div>
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${c.color}22` }}>
                <c.icon className="w-5 h-5" style={{ color: c.color }} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {(critical.length > 0 || warning.length > 0) && (
        <Card className="p-5 border-l-4" style={{ borderLeftColor: critical.length ? "#EF4444" : "#F59E0B" }}
          data-testid="stock-alert-banner">
          <div className="flex items-start gap-3">
            <TrendingDown className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#EF4444" }} />
            <div className="text-sm">
              <span className="font-semibold">Dikkat: </span>
              {critical.map((m) => (
                <span key={m.material_id}>
                  <b style={{ color: "#EF4444" }}>{m.name}</b> stoğu {m.runout_week} haftasında tükenecek.{" "}
                </span>
              ))}
              {warning.map((m) => (
                <span key={m.material_id}>
                  <b style={{ color: "#F59E0B" }}>{m.name}</b> emniyet stoğunun altına düşecek.{" "}
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-head text-xl font-semibold">Malzeme Stok Analizi</h2>
          <div className="flex gap-1.5 flex-wrap" data-testid="analysis-type-tabs">
            {[
              { k: "", l: "Tümü" }, { k: "tel", l: "Tel" }, { k: "tela", l: "Tela" },
              { k: "kraft", l: "Kraft" }, { k: "lamet", l: "Lamet" }, { k: "cring", l: "C-Ring" },
              { k: "holtmelt", l: "Holtmelt" }, { k: "celik_cember", l: "Çelik Çember" },
              { k: "tahta_palet", l: "Tahta Palet" }, { k: "round_frame", l: "Round Frame" },
            ].map((t) => (
              <button key={t.k} data-testid={`analysis-tab-${t.k || "all"}`} onClick={() => setTab(t.k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  tab === t.k ? "bg-[#2563EB] text-white" : "bg-[#0F1520] border border-[#212D40] text-[#94A3B8] hover:text-white"
                }`}>
                {t.l}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40]">
                <th className="py-3 pr-4">Malzeme</th>
                <th className="py-3 pr-4 text-right">Mevcut Stok</th>
                <th className="py-3 pr-4 text-right">4 Hf İhtiyaç</th>
                {(data.horizon || []).slice(0, 3).map((h) => (
                  <th key={h.label} className="py-3 pr-4 text-right">{h.label.split("-H")[1]}. Hf</th>
                ))}
                <th className="py-3 pr-4 text-right">4 Hf Sipariş</th>
                <th className="py-3 pr-4">Tükenme Haftası</th>
                <th className="py-3 pr-4">Durum</th>
              </tr>
            </thead>
            <tbody>
              {data.materials.filter((m) => !tab || m.type === tab).map((m) => {
                const s = STATUS[m.status];
                return (
                  <tr key={m.material_id} className="border-b border-[#1a2334] hover:bg-[#0F1520]"
                    data-testid={`dashboard-material-row-${m.material_id}`}>
                    <td className="py-3 pr-4 font-medium">{m.name}</td>
                    <td className="py-3 pr-4 text-right font-num">{fmt(m.current_stock)} {m.unit}</td>
                    <td className="py-3 pr-4 text-right font-num">{MANUAL_TYPES.includes(m.type) ? "-" : `${fmt(sum4(m.weekly, "consumption"))} ${m.unit}`}</td>
                    {(data.horizon || []).slice(0, 3).map((h, i) => (
                      <td key={h.label} className="py-3 pr-4 text-right font-num text-[#94A3B8]">{MANUAL_TYPES.includes(m.type) ? "-" : fmt((m.weekly?.[i]?.consumption) || 0)}</td>
                    ))}
                    <td className="py-3 pr-4 text-right font-num text-[#10B981]">{fmt(sum4(m.weekly, "incoming"))} {m.unit}</td>
                    <td className="py-3 pr-4 font-num">
                      {m.runout_week || <span className="text-[#10B981]">—</span>}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                        style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>{s.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {tab ? (
              <tfoot>
                <tr className="border-t-2 border-[#212D40] font-semibold" data-testid="dashboard-totals-row">
                  <td className="py-3 pr-4">TOPLAM</td>
                  <td className="py-3 pr-4 text-right font-num">{fmt(data.materials.filter((m) => m.type === tab).reduce((a, m) => a + (m.current_stock || 0), 0))}</td>
                  <td className="py-3 pr-4 text-right font-num">{fmt(data.materials.filter((m) => m.type === tab).reduce((a, m) => a + sum4(m.weekly, "consumption"), 0))}</td>
                  {(data.horizon || []).slice(0, 3).map((h, i) => (
                    <td key={h.label} className="py-3 pr-4 text-right font-num">{fmt(data.materials.filter((m) => m.type === tab).reduce((a, m) => a + ((m.weekly?.[i]?.consumption) || 0), 0))}</td>
                  ))}
                  <td className="py-3 pr-4 text-right font-num text-[#10B981]">{fmt(data.materials.filter((m) => m.type === tab).reduce((a, m) => a + sum4(m.weekly, "incoming"), 0))}</td>
                  <td className="py-3 pr-4"></td>
                  <td className="py-3 pr-4"></td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </Card>
    </div>
  );
}
